import { CartUpdateEvent } from '@theme/events';

/**
 * Drives the depleting bar under the cart drawer reservation timer.
 * Reads the countdown text rendered by cart-countdown.js, so no timer state is duplicated.
 */
function updateTimerBars() {
  document.querySelectorAll('.cart-drawer--jooms [data-cart-countdown-root]').forEach((root) => {
    const display = root.querySelector('[data-cart-countdown-display]');
    const minutes = Number(root.getAttribute('data-cart-countdown-minutes')) || 15;
    const match = display?.textContent?.trim().match(/^(\d+):(\d{2})$/);
    if (!(root instanceof HTMLElement) || !match) return;

    const remaining = Number(match[1]) * 60 + Number(match[2]);
    const progress = Math.max(0, Math.min(1, remaining / (minutes * 60)));
    root.style.setProperty('--jooms-timer-progress', progress.toFixed(4));
    root.classList.toggle('is-urgent', remaining <= 120);
  });
}

updateTimerBars();
setInterval(updateTimerBars, 1000);

/**
 * "Upgrade to 2 bottles" offer. Rebuilds the cart lines exactly the way the Rapid Bundles widget adds
 * its "Buy 2 get 1" tier, so its discounts treat them as one bundle:
 *  - the bottles carry `__rapi_offer: {"main":true,"id":<group>,"offer":<tier>}`
 *  - each progress-bar gift carries `__rapi_offer: {"id":<group>,"offer":<tier>,"prgs":true,"pidx":N}`
 * Gifts without that shared group/tier stay full price, and Rapid Bundles only adds them from its
 * product-page widget, so we add them here.
 *
 * Two requests: remove the old lines, then add the bundle with the drawer section rendered in the
 * same response.
 */

/** @param {string} url @param {object} body */
async function cartPost(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`${url} failed with ${response.status}`);
  return response.json();
}

/** @param {string | undefined} value @param {any} fallback */
function parseJSON(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function groupId() {
  return Math.random().toString(36).slice(2, 6);
}

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-jooms-upgrade]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const data = button.dataset;
  const sectionId = button.closest('cart-items-component')?.getAttribute('data-section-id');
  const quantity = Number(data.joomsUpgrade) || 3;
  const unitPrice = Number(data.joomsUnitPrice) || 0;
  /** @type {Array<{key: string, qty: number}>} */
  const removeLines = parseJSON(data.joomsRemove, []);
  /** @type {Array<{id: number, min: number, pidx: number}>} */
  const gifts = parseJSON(data.joomsGifts, []);
  if (!data.joomsVariant || !removeLines.length) return;

  const label = button.textContent;
  button.disabled = true;
  button.classList.add('is-loading');
  button.textContent = data.joomsLoading || 'Upgrading…';

  try {
    const group = groupId();
    const offer = data.joomsOffer || '';
    const bundle = data.joomsBundle || '';
    // Buy 2 get 1: the shopper pays for quantity - 1 bottles, which is what unlocks the gifts.
    const spend = unitPrice * Math.max(quantity - 1, 1);
    const unlocked = gifts.filter((gift) => gift.id && spend >= gift.min);

    const items = [
      {
        id: Number(data.joomsVariant),
        quantity,
        properties: {
          _rapi_bundles: bundle,
          _rapi_market: data.joomsMarket || 'US',
          __rapi_offer: JSON.stringify({ main: true, id: group, offer }),
        },
      },
      ...unlocked.map((gift) => ({
        id: gift.id,
        quantity: 1,
        properties: {
          _rapi_bundles: bundle,
          __rapi_offer: JSON.stringify({ id: group, offer, prgs: true, pidx: gift.pidx }),
        },
      })),
    ];

    await cartPost('/cart/update.js', {
      updates: Object.fromEntries(removeLines.map((line) => [line.key, 0])),
    });
    const sections = sectionId ? [sectionId] : [];
    // The old line is already gone, so never leave the shopper without their bottles.
    const added = await cartPost('/cart/add.js', { items, sections })
      .catch(() => cartPost('/cart/add.js', { items: items.slice(0, 1), sections }))
      .catch(() => cartPost('/cart/add.js', { items: [{ id: Number(data.joomsVariant), quantity: 1 }], sections }));

    const removedQty = removeLines.reduce((sum, line) => sum + line.qty, 0);
    const addedQty = items.reduce((sum, item) => sum + item.quantity, 0);
    const itemCount = (Number(data.joomsCount) || 0) - removedQty + addedQty;

    document.dispatchEvent(
      new CartUpdateEvent(added, 'jooms-upgrade', { source: 'jooms-upgrade', itemCount, sections: added.sections })
    );
  } catch (error) {
    console.error('[jooms] upgrade failed', error);
    button.disabled = false;
    button.classList.remove('is-loading');
    button.textContent = label;
  }
});
